<script setup>
// FLOW-06 (SPEC S4, R14, H2; PLAN G15, G25, G28; Tasks 6.1 e 6.7) — a página de uma execução de
// produção, dentro da seção Execuções da aba PowerFlow: busca o detalhe (`GET
// /powerflow/execucoes/:id`) e o entrega ao detalhe aprovado no mockup (`ExecucaoDetalhe`), que só
// mostra e repassa as ações.
//
// **As ações** seguem a régua: reexecutar e cancelar são de quem vê a aba (que é quem chega aqui); abrir
// no editor é de quem monta flow (`useMontaFlow`: o super-admin, ou o gestor com a chave de montar
// flow), e leva o `?execucao=` para o editor abrir o canvas aceso com o que esta execução guardou.
// - **Reexecutar** do início (as duas formas `inicio_*`) cria uma execução nova, e a página vai até
//   ela; do passo que falhou, é a mesma execução que volta a rodar, e a página fica e relê.
// - **Cancelar** (o diálogo confirma antes) pede ao servidor e relê: o passo em curso termina, e o
//   estado vira `cancelada` quando ele acaba. A que já terminou responde 409, e a página relê também.
// - A reexecução que veio de outra leva à de origem.
// Toda falha de ação vai num alerta, com a frase do servidor quando ele a dá. A recusa do reexecutar
// leva o erro de leigo inteiro (D19), a frase e o como resolver: é ele que diz o que fazer, como no
// 409 `detalhe_apagado` da execução podada ("use Executar agora").
// ACESSO-01 (L11b): reexecutar e cancelar (`powerflow_execucoes#reexecutar` e `#cancelar`) pedem o
// `powerflow:run` no servidor. Com a chave ACESSO_GRANULAR ligada, o detalhe só os mostra a quem o tem
// (`executa`, o `podeExecutar` dele), e a recusa do acesso diz a frase dela; desligada, como hoje.
//
// **Ao vivo** (S4; `execucoesAoVivo.js`): a página assina o canal da conta e aplica os eventos desta
// execução. A mudança de estado entra nos campos dela, e o passo (aninhado em `passo`, G28) entra no
// lugar dele, pelo caminho. O evento não traz a saída do passo, e a execução que termina tem a
// resposta e o que o reexecutar precisa (G25): nos dois casos, o detalhe é relido, num lote só por
// segundo. Enquanto ela roda, o detalhe é relido também devagar (a cada 15 s), porque um lote de
// eventos pode se perder no caminho; sem cabo, a cada 3 s. A cada (re)conexão, ele é relido.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMontaFlow } from 'dashboard/composables/useMontaFlow';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';
import powerflowExecucoesApi from 'dashboard/api/powerflowExecucoes';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ExecucaoDetalhe from './ExecucaoDetalhe.vue';
import PainelDeFalha from './PainelDeFalha.vue';
import { VIVAS, aplicarNoDetalhe, assinarExecucoes } from './execucoesAoVivo';

const props = defineProps({
  execucaoId: { type: String, required: true },
});

const ESPERA_DA_RELEITURA = 1000;
const SONDAGEM_COM_CABO = 15000;
const SONDAGEM_SEM_CABO = 3000;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { montaFlow } = useMontaFlow();
const { decide } = useAcesso();

const accountId = computed(() => Number(route.params.accountId));
const executa = computed(() => decide('powerflow:run', () => true));

const execucao = ref(null);
const falha = ref(null);
const agindo = ref(false);

const corpoDoErro = erro => erro?.response?.data || {};
const mensagemDoServidor = erro =>
  corpoDoErro(erro).erro?.mensagem || corpoDoErro(erro).mensagem || null;
const erroDeLeigo = erro =>
  [mensagemDoServidor(erro), corpoDoErro(erro).erro?.como_resolver]
    .filter(Boolean)
    .join(' ') || null;

// -- ao vivo
let assinatura = null;
let semCabo = false;
let sondagem = null;
let releitura = null;

const viva = () => VIVAS.includes(execucao.value?.estado);
const intervaloDaSondagem = () =>
  semCabo ? SONDAGEM_SEM_CABO : SONDAGEM_COM_CABO;

// O detalhe de novo, sem tirar a tela do lugar; enquanto ela roda, a próxima leitura fica marcada. A
// falha fica para a próxima leitura.
async function reler() {
  clearTimeout(sondagem);
  sondagem = null;
  const id = props.execucaoId;
  try {
    const detalhe = await powerflowExecucoesApi.show(accountId.value, id);
    if (id === props.execucaoId) execucao.value = detalhe;
  } catch {
    // O que está na tela continua.
  }
  if (id === props.execucaoId && viva()) {
    sondagem = setTimeout(reler, intervaloDaSondagem());
  }
}

// A primeira leitura marcada: depois do detalhe, e quando o cabo cai.
function agendarSondagem() {
  clearTimeout(sondagem);
  sondagem = viva() ? setTimeout(reler, intervaloDaSondagem()) : null;
}

function pedirReleitura() {
  if (releitura) return;
  releitura = setTimeout(() => {
    releitura = null;
    reler();
  }, ESPERA_DA_RELEITURA);
}

function receber(eventos) {
  if (!execucao.value) return;
  const { detalhe, reler: precisa } = aplicarNoDetalhe(execucao.value, eventos);
  execucao.value = detalhe;
  if (precisa) pedirReleitura();
}

// -- o detalhe
async function carregar() {
  const id = props.execucaoId;
  execucao.value = null;
  falha.value = null;
  try {
    const detalhe = await powerflowExecucoesApi.show(accountId.value, id);
    if (id !== props.execucaoId) return;
    execucao.value = detalhe;
    agendarSondagem();
  } catch (erro) {
    if (id !== props.execucaoId) return;
    const naoExiste = erro?.response?.status === 404;
    falha.value = {
      mensagem: naoExiste
        ? mensagemDoServidor(erro) ||
          t('POWER_TOOLS.POWERFLOW.DETAIL.NOT_FOUND')
        : t('POWER_TOOLS.POWERFLOW.DETAIL.LOAD_ERROR'),
      podeTentar: !naoExiste,
    };
  }
}

// A reexecução, a origem e o fim de uma reexecução levam a outra execução: a mesma página, outro id.
watch(
  () => props.execucaoId,
  () => {
    clearTimeout(sondagem);
    clearTimeout(releitura);
    releitura = null;
    carregar();
  }
);

// -- as ações
const paraAExecucao = execucaoId => ({
  name: 'powerflow_execucao',
  params: { accountId: accountId.value, execucaoId },
});

function voltar() {
  router.push({
    name: 'powerflow_execucoes',
    params: { accountId: accountId.value },
  });
}

function abrirNoEditor() {
  router.push({
    name: 'powerflow_flow_editor',
    params: { accountId: accountId.value, id: execucao.value.flow.entrada_id },
    query: { execucao: execucao.value.execucao_id },
  });
}

async function reexecutar(forma) {
  if (agindo.value) return;
  agindo.value = true;
  try {
    const nova = await powerflowExecucoesApi.reexecutar(
      accountId.value,
      props.execucaoId,
      forma
    );
    if (forma === 'passo_que_falhou') {
      useAlert(t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_RESUMED'));
      await reler();
    } else {
      router.push(paraAExecucao(nova.execucao_id));
    }
  } catch (erro) {
    useAlert(
      fraseDaRecusa(erro) ||
        erroDeLeigo(erro) ||
        t('POWER_TOOLS.POWERFLOW.DETAIL.RERUN_ERROR')
    );
  } finally {
    agindo.value = false;
  }
}

async function cancelar() {
  if (agindo.value) return;
  agindo.value = true;
  try {
    await powerflowExecucoesApi.cancelar(accountId.value, props.execucaoId);
    useAlert(t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_REQUESTED'));
  } catch (erro) {
    const terminou = erro?.response?.status === 409;
    useAlert(
      fraseDaRecusa(erro) ||
        mensagemDoServidor(erro) ||
        (terminou
          ? t('POWER_TOOLS.POWERFLOW.DETAIL.ALREADY_FINISHED')
          : t('POWER_TOOLS.POWERFLOW.DETAIL.CANCEL_ERROR'))
    );
  } finally {
    agindo.value = false;
  }
  await reler();
}

const abrirOrigem = origemId => router.push(paraAExecucao(origemId));

onMounted(() => {
  assinatura = assinarExecucoes(accountId.value, {
    aoReceber: receber,
    aoConectar: () => {
      if (execucao.value) reler();
    },
    aoRecusar: () => {
      assinatura = null;
      semCabo = true;
      agendarSondagem();
    },
  });
  semCabo = !assinatura;
  carregar();
});

onBeforeUnmount(() => {
  assinatura?.unsubscribe();
  clearTimeout(sondagem);
  clearTimeout(releitura);
});
</script>

<template>
  <div class="flex-1 min-w-0 px-6 pt-4 pb-8 overflow-y-auto">
    <div class="flex flex-col max-w-6xl gap-4">
      <div>
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-arrow-left"
          data-voltar-as-execucoes
          :label="t('POWER_TOOLS.POWERFLOW.DETAIL.BACK')"
          @click="voltar"
        />
      </div>

      <PainelDeFalha
        v-if="falha"
        :mensagem="falha.mensagem"
        :pode-tentar="falha.podeTentar"
        @tentar="carregar"
      />
      <ExecucaoDetalhe
        v-else-if="execucao"
        :execucao="execucao"
        :pode-executar="executa"
        :pode-editar="montaFlow"
        @abrir-no-editor="abrirNoEditor"
        @reexecutar="reexecutar"
        @cancelar="cancelar"
        @abrir-origem="abrirOrigem"
      />
      <div
        v-else
        role="status"
        class="flex items-center justify-center gap-2 px-6 py-12 text-sm border rounded-pz border-n-weak bg-n-card shadow-pz text-n-slate-11"
        data-execucao-carregando
      >
        <Spinner />
        {{ t('POWER_TOOLS.POWERFLOW.DETAIL.LOADING') }}
      </div>
    </div>
  </div>
</template>
